import CheckIcon from "@mui/icons-material/Check";
import { type FC } from "react";
import {
	Box,
	Stack,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import { useClosingJobs } from "@/views/closing/hooks/use-closing-jobs";
import { useShowMoreLess } from "@/hooks/use-show-more-less";
import { ShowMoreLessToggle } from "@/components/show-more-less-toggle";
import { useDownstreamMachineAliasLookup } from "@/views/closing/hooks/use-downstream-machine-alias-lookup";
import { getClosingStatus, getSkeletonTableRows } from "@/views/closing/utils";
import { EmptyState } from "@/components/empty-state";
import { FailedRetry } from "@/components/failed-retry";
import { type ClosingCarton } from "@/schemas";
import { HistoryLink } from "@/components/history-link";

const ClosedTableCell = styled(TableCell)(({ theme }) => ({
	color: theme.palette.success.main,
}));

export const ClosingTable: FC = () => {
	const intl = useIntl();

	const {
		data: closingJobsData,
		isLoading: activeCartonsLoading,
		error,
	} = useClosingJobs({
		filter: null,
		pageNumber: 1,
		pageSize: 10,
	});

	const { getMachineAlias } = useDownstreamMachineAliasLookup();

	const activeCartons = closingJobsData?.results || [];
	const totalActiveCartons = closingJobsData?.totalResults || 0;

	const { isExpanded, toggle, itemsToDisplay, shouldShowToggle } = useShowMoreLess<ClosingCarton>({
		items: activeCartons,
		threshold: 2,
	});

	if (error) {
		return <FailedRetry retry={() => window.location.reload()} />;
	}

	return (
		<TableContainer sx={{ borderBottom: 0, borderRadius: 0, minHeight: 208 }}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell colSpan={4} sx={{ padding: 2 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
								}}
							>
								<Typography variant="h5">
									<FormattedMessage
										id="Closing ({totalResults})"
										defaultMessage="Closing ({totalResults})"
										values={{ totalResults: totalActiveCartons }}
									/>
								</Typography>
								<HistoryLink />
							</Box>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody sx={{ borderBottom: "none" }}>
					{activeCartonsLoading === true && getSkeletonTableRows(2, 4)}
					{totalActiveCartons === 0 && !activeCartonsLoading ? (
						<TableRow>
							<TableCell colSpan={4} align="center" sx={{ borderBottom: "none" }}>
								<EmptyState iconFontSize={80}>
									<Typography variant="body2" color="text.secondary">
										<FormattedMessage
											id="Nothing is Being Closed Right Now"
											defaultMessage="Nothing is Being Closed Right Now"
										/>
									</Typography>
								</EmptyState>
							</TableCell>
						</TableRow>
					) : (
						itemsToDisplay.map((carton) => {
							const Cell = carton.status === "Closed" ? ClosedTableCell : TableCell;
							return (
								<TableRow key={carton.packagingSolutionId}>
									<Cell sx={{ minWidth: 150 }}>
										<Typography variant="body2">{carton.alias}</Typography>
									</Cell>
									<Cell sx={{ minWidth: 150 }}>
										<Typography variant="body2">{carton.identifier}</Typography>
									</Cell>
									<Cell>
										<Typography variant="body2">{getMachineAlias(carton.closingMachineId)}</Typography>
									</Cell>
									<Cell sx={{ minWidth: 150 }}>
										<Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
											{carton.status === "Closed" && <CheckIcon fontSize="small" />}
											{getClosingStatus(carton.status, intl)}
										</Stack>
									</Cell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>
			{shouldShowToggle === true && <ShowMoreLessToggle isExpanded={isExpanded} onToggle={toggle} />}
		</TableContainer>
	);
};
